import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import { useSelector } from 'react-redux'
import Dialog from '@material-ui/core/Dialog'

import styles from './styles.module.scss'

import {
  getCompanyTimeOffPolicies,
  getCompanyTimeOffRequests,
  getCompanyEmployeesAll,
  getPlaces,
  getCompanyTimeOffs,
  getCompanyWorkTimeSettings,
  updateRequestStatus,
  getCompanyInfo,
  updateRequestStatusBulk,
  updateRequest,
} from '../../../api'
import { generateResourcesFromEmployees, getEmployeesFromResources, getCheckedEmployeeIds } from '../Calendar/utils'
import { userSelector } from '../../../store/auth/selectors'
import usePermissions from '../../Core/usePermissions'

import CustomSelect from '../../Core/CustomSelect'
import DRP from '../../Core/DRP/DRP'
import SearchIcon from '../../Icons/SearchIcon'
import Input from '../../Core/Input/Input'
import Progress from '../../Core/Progress'
import StyledCheckbox from '../../Core/Checkbox/Checkbox'
import InfoIcon from '../../Icons/InfoIcon'
import CheckboxIcon from '../../Icons/CheckboxIcon'
import RequestBehalf from '../../Core/Dialog/RequestBehalf'
import Table from '../Table'
import PolicySymbol from '../PolicySymbol'
import DescriptionIcon from '../../Icons/DescriptionIcon'
import EditIconFixedFill from '../../Icons/EditIconFixedFill'
import PaperClipIcon from '../../Icons/PaperClip'
import CheckIcon from '../../Icons/CheckIcon'
import RejectIcon from '../../Icons/RejectIcon'
import PendingIcon from '../../Icons/PendingIcon'
import ApprovedIcon from '../../Icons/ApprovedIcon'
import SuspendedIcon from '../../Icons/SuspendedIcon'
import UploadFiles from '../../UploadFiles'

const moment = extendMoment(Moment)

const convertFormat = (companyFormat) => {
  const keyMap = {
    'DD': 'DD',
    'MM': 'MM',
    'YY': 'YYYY',
  }
  return companyFormat.split('.').map(k => keyMap[k] || k).join('-')
}

const generateSections = (requests, keyFormat) => {
  const keyMap = {
    'DD': 'DD',
    'MM': 'MMMM',
    'YY': 'YYYY',
  }
  const locale = localStorage.getItem('i18nextLng') || 'en'
  const format = keyFormat.split('.').map(k => keyMap[k] || k).join(' ')
  const sections = requests.reduce((acc, request) => {
    const sectionKey = request.status === 'pending' ? 'pending' : moment(request.created_at).format('YYYY-MM-DD')
    if (!acc[sectionKey]) {
      acc[sectionKey] = []
    }
    acc[sectionKey].push(request)
    return acc
  }, {})

  return Object.entries(sections).sort((a, b) => {
    if (a[0] === 'Pending') return -1
    if (b[0] === 'Pending') return 1
    return moment(b[0]).toDate() - moment(a[0]).toDate()
  }).reduce((acc, [key, value]) => {
    const formattedKey = key === 'pending' ? 'pending' : moment(key).locale(locale).format(`dddd, ${format}`)
    return {
    ...acc,
      [formattedKey]: value,
    }
  }, {})
}

const filterRequests = (request, query) => {
  if (!query) return true
  const q = query.toLowerCase()
  if (request.employee.title.toLowerCase().includes(q)) return true
  if (request.policy.name.toLowerCase().includes(q)) return true
  return false
}

const filterStatuses = (request, statuses) => {
  return statuses.includes(request.status)
}

const TimneOffRequests = ({companyId}) => {
  const { t } = useTranslation()

  const permissions = usePermissions([{
    name: 'app_manager',
    permission: 'app_manager'
  }])

  const user = useSelector(userSelector)

  const isManager = permissions.app_manager

  const requestFormRef = useRef(null)
  const uploadFilesRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [companyData, setCompanyData] = useState({})
  const [query, setQuery] = useState('')
  const [{employees, policies, places, statuses, filterDate}, setFilters] = useState({
    filterDate: {
      start: moment().startOf('isoWeek'),
      end: moment().endOf('isoWeek'),
    },
    places: [],
    policies: [],
    employees: [],
    statuses: ['pending', 'approved', 'rejected'],
  })
  const [sections, setSections] = useState({})
  const [workTimeSettings, setWorkTimeSettings] = useState({})
  const [showDocuments, setShowDocuments] = useState(false)

  const holidaysMap = [...(workTimeSettings.work_time?.holidays || []), ...(workTimeSettings.national_holidays || [])].reduce((acc, holiday) => ({
    ...acc,
    [holiday.date]: true,
  }), {})

  const timeOffRequests = Object.values(sections).flat(Infinity)

  const isCheckedAll = timeOffRequests.length > 0 && timeOffRequests.every(r => r.checked)
  const checkedItems = timeOffRequests.filter(r => r.checked)

  const columns = [
    { label: '', accessor: 'checkbox', className: styles.center },
    { label: t('Action'), accessor: 'action', className: styles.actions },
    { label: t('Status'), accessor: 'status' },
    { label: t('Employee'), accessor: 'employee.title' },
    { label: t('Request type'), accessor: 'policy.name', className: styles.policy, width: 170 },
    { label: t('Days'), accessor: 'totalRestDays' },
    { label: t('When'), accessor: 'from', width: 200 },
    { label: t('Requested on'), accessor: 'created_at' },
    { label: t('Group'), accessor: 'groups' },
    { label: t('Sub-group'), accessor: 'subgroups' },
    { label: t('Place'), accessor: 'employee.place' },
    { label: t('1st approver'), accessor: 'approver_1_name' },
    { label: t('2nd approver'), accessor: 'approver_2_name' },
  ]
  
  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (policies.length && employees.length) {
      getTimeOffRequests()
    } else {
      setLoading(false)
    }
  }, [places, employees, policies, filterDate])

  const init = async () => {
    const [employeesRes, policiesRes, placesRes, timeOffsRes, workTimesRes, companyRes] = await Promise.all([
      getCompanyEmployeesAll(companyId),
      getCompanyTimeOffPolicies(companyId),
      getPlaces(companyId),
      getCompanyTimeOffs(companyId),
      getCompanyWorkTimeSettings(companyId),
      getCompanyInfo(companyId),
    ])
    
    const data = {employees: [], policies: [], places: []}
    if (Array.isArray(employeesRes?.users)) {
      const grouped = generateResourcesFromEmployees(employeesRes?.users)
      data.employees = grouped
    }
    if (Array.isArray(policiesRes?.policies)) {
      const timeOffsMap = timeOffsRes.reduce((acc, timeOff) => ({
        ...acc,
        [timeOff.id]: timeOff,
      }), {})
      data.policies = policiesRes.policies.map(p => ({...p, checked: false, title: p.name, isEmployee: true, time_off: timeOffsMap[p.time_off_id]}))
    }
    if (Array.isArray(placesRes)) {
      data.places = placesRes.map(p => ({...p, checked: false, title: p.name, isEmployee: true}))
    }
    setCompanyData(companyRes)
    setWorkTimeSettings(workTimesRes)
    setFilters(prev => ({...prev, ...data}))
  }

  const getTimeOffRequests = async () => {
    setLoading(true)
    const params = {
      start_date: filterDate.start.format('YYYY-MM-DD'),
      end_date: filterDate.end.format('YYYY-MM-DD'),
      employees: getCheckedEmployeeIds(employees),
      policies: policies.filter(p => p.checked).map(p => p.id),
      places: places.filter(p => p.checked).map(p => p.id),
    }
    const res = await getCompanyTimeOffRequests(companyId, params)
    if (Array.isArray(res?.request_behalf)) {
      const policiesMap = policies.reduce((acc, policy) => ({
        ...acc,
        [policy.id]: policy,
      }), {})
      const emplloyeesList = getEmployeesFromResources(employees)
      const employeesMap = emplloyeesList.reduce((acc, emp) => ({
        ...acc,
        [emp.id]: emp,
      }), {})
      const timeOffReuests = res.request_behalf.filter(r => policiesMap[r.policy_id]).map(r => {
        const policy = policiesMap[r.policy_id]
        const range = Array.from(moment.range(moment(r.from), moment(r.to)).by('days')).map(date => date.format('YYYY-MM-DD'))
        const totalRestDays = range.reduce((acc, date) => {
          if (holidaysMap[date]) {
            return acc
          }
          const day = moment(date).day()
          if (policy.time_off.work_days === 'any_day' || (day !== 0 && day !== 6)) {
            return acc + 1
          }
          return acc
        }, 0)
        return {
          ...r,
          policy: policy,
          employee: employeesMap[r.employee_id],
          approver: employeesMap[r.approved_by],
          totalRestDays: totalRestDays,
          checked: false,
        }
      })
      const sections = generateSections(timeOffReuests, companyData.date_format)
      setSections(sections)
    }
    setLoading(false)
  }

  const handleChangeFilter = (key, data) => {
    setFilters(prev => ({
      ...prev,
      [key]: data,
    }))
  }

  const handleChangeDate = (res) => {
    setFilters(prev => ({
      ...prev,
      filterDate: {
        start: moment(res.startDate),
        end: moment(res.endDate),
      },
    }))
  }

  const handleChangeRequestStatus = async (request, status, employeeIds) => {
    const updated = timeOffRequests.map(r => r.id === request.id ? ({...r, status: status, checked: false}) : r)
    setSections(generateSections(updated, companyData.date_format))
    const res = await updateRequestStatus(companyId, request.time_off_id, request.policy_id, request.id, {status: status, employees: employeeIds})
    if (!res) {
      getTimeOffRequests()
    }
  }

  const handleChangeRequestStatusBulk = async (status, checkedRequests) => {
    const updated = timeOffRequests.map(r => checkedRequests.some(cr => cr.id === r.id) ? ({...r, status: status, checked: false}) : r)
    setSections(generateSections(updated, companyData.date_format))
    const request_behalf_ids = checkedRequests.map(r => r.id)
    const res = await updateRequestStatusBulk(companyId, {status: status, request_behalf_ids: request_behalf_ids})
    if (!res) {
      getTimeOffRequests()
    }
  }

  const handleToggleAll = () => {
    if (isCheckedAll) {
      setSections(generateSections(timeOffRequests.map(r => ({...r, checked: false})), companyData.date_format))
      return
    }
    setSections(generateSections(timeOffRequests.map(r => {
      const isApprover = (isManager && !r.approver_1_id && !r.approver_2_id || (r.approver_1_id === user.employee?.id || r.approver_2_id === user.employee?.id))
      const isSelfRequest = r.employee_id === user.employee?.id
      const isCanApproveSelfRequest = !r.policy.prevent_self_approval && isApprover
      const canApprove = isSelfRequest ? isCanApproveSelfRequest : isApprover
      return {...r, checked: canApprove}
    }), companyData.date_format))
  }

  const handleSelect = (request) => () => {
    setSections(generateSections(timeOffRequests.map(r => r.id === request.id ? {...r, checked: !r.checked} : r), companyData.date_format))
  }

  const handleSubmitRequest = async (data) => {
    const post = {
      ...data,
      employees: [data.employee_id],
    }
    await updateRequest(companyId, data.time_off_id, data.policy_id, data.id, post)
    requestFormRef.current.close()
    getTimeOffRequests()
  }

  const handleEdit = (params) => {
    requestFormRef.current.open(params)
  }

  const handleFilterStatus = (status) => () => {
    setFilters(prev => ({
      ...prev,
      statuses: prev.statuses.includes(status) ? prev.statuses.filter(s => s !== status) : [...prev.statuses, status],
    }))
  }

  const handleOpenDocuments = (documents) => () => {
    setShowDocuments(true)
    setTimeout(() => {
      uploadFilesRef.current.setFiles(documents)
    }, 300)
  }

  const renderCellHeader = (props) => {
    switch (props.accessor) {
      case 'checkbox':
        return (
          <StyledCheckbox
            id='all'
            checked={isCheckedAll}
            onChange={handleToggleAll} />
        )
      case 'status':
        return (
          <div className={styles.statusHeader}>
            {props.label}
            <div className={cn(styles.statusButton, {[styles.active]: statuses.includes('pending')})} onClick={handleFilterStatus('pending')}>
              <PendingIcon />
            </div>
            <div className={cn(styles.statusButton, {[styles.active]: statuses.includes('approved')})} onClick={handleFilterStatus('approved')}>
              <ApprovedIcon />
            </div>
            <div className={cn(styles.statusButton, {[styles.active]: statuses.includes('rejected')})} onClick={handleFilterStatus('rejected')}>
              <SuspendedIcon />
            </div>
          </div>
        )
      default:
        return props.label
    }
  }

  const renderCell = (value, request, col) => {
    const isApprover = (isManager && !request.approver_1_id && !request.approver_2_id || (request.approver_1_id === user.employee?.id || request.approver_2_id === user.employee?.id))
    const isSelfRequest = request.employee_id === user.employee?.id
    const isCanApproveSelfRequest = !request.policy.prevent_self_approval && isApprover
    const canApprove = isSelfRequest ? isCanApproveSelfRequest : isApprover
    switch (col.accessor) {
      case 'checkbox':
        return (
          <StyledCheckbox
            id={request.id}
            checked={request.checked}
            disabled={!canApprove}
            onChange={handleSelect(request)} />
        )
      case 'action':
        return (
          <>
          <div data-tooltip-html={t("Edit")} data-tooltip-id="note" className={styles.icon} onClick={() => handleEdit(request)}>
            <EditIconFixedFill />
          </div>
          { request.status !== 'approved' && canApprove && (
            <div data-tooltip-html={t("Approve")} data-tooltip-id="note" className={cn(styles.icon, styles.approve)} onClick={() => handleChangeRequestStatus(request, 'approved', [request.employee_id])}>
              <CheckIcon />
            </div>
          )}
          { request.status !== 'rejected' && canApprove && (
            <div data-tooltip-html={t("Reject")} data-tooltip-id="note" className={cn(styles.icon, styles.reject)} onClick={() => handleChangeRequestStatus(request, 'rejected', [request.employee_id])}>
              <RejectIcon />
            </div>
          )}
          </>
        )
      case 'status':
        return (
          <div className={cn(styles.status, styles[request.status])}>
            { t(request.status) }
          </div>
        )
      case 'policy.name':
        return (
          <>
            <PolicySymbol symbol={request.policy.symbol} color={request.policy.color} />
            {request.policy.name}
            {
              request.note
                ? <div className={styles.note} data-tooltip-html={request.note} data-tooltip-id='note'>
                    <DescriptionIcon width={12} height={12} className={styles.noteIcon} />
                  </div>
                : null
            }
            {
              request.documents?.length
                ? <div className={styles.documentsIcon} onClick={handleOpenDocuments(request.documents)}>
                    <PaperClipIcon />
                  </div>
                : null
            }
          </>
        )
      case 'from':
        const formattedFrom = moment(request.from).format(convertFormat(companyData.date_format))
        const formattedTo = moment(request.to).format(convertFormat(companyData.date_format))
        return `${formattedFrom} - ${formattedTo}`
      case 'created_at':
        return moment(request.created_at).format(convertFormat(companyData.date_format))
      default:
        return value
    }
  }

  const filteredSections = Object.entries(sections).map(([section, requests]) => {
    return [section, requests.filter(r => filterRequests(r, query) && filterStatuses(r, statuses))]
  }).filter(([_, requests]) => requests.length)

  return (
    <div className={styles.container}>
      <div className={styles.screen}>
        <div className={styles.toolsContainer}>
          <DRP
            initRange={{startDate: filterDate.start.toDate(), endDate: filterDate.end.toDate()}}
            onChange={handleChangeDate} />
          <div style={{flex: 1}}>
            <Input
              icon={<SearchIcon />}
              placeholder={`${t('Search')}...`}
              width="100%"
              height='36px'
              value={query}
              onChange={(e) => setQuery(e.target.value)} />
          </div>
          <CustomSelect
            placeholder={t('All places')}
            items={places}
            onChange={data => handleChangeFilter('places', data)}
            width='auto' />
          <CustomSelect
            placeholder={t('All policies')}
            items={policies}
            onChange={data => handleChangeFilter('policies', data)}
            width='auto' />
          <CustomSelect
            placeholder={t('All employees')}
            buttonLabel={t('Filter')}
            items={employees}
            onChange={data => handleChangeFilter('employees', data)}
            width='auto' />
        </div>
        <Table
          columns={columns}
          renderCellHeader={renderCellHeader}
          renderCell={renderCell}
          data={filteredSections} />
      </div>
      <div className={cn(styles.sidebarContainer, {[styles.active]: timeOffRequests.some(r => r.checked)})}>
        <div className={styles.header}>
          <InfoIcon />
          { t('Multiple entries selection') }
        </div>
        <div className={styles.sidebarContent}>
          <CheckboxIcon className={styles.checkboxIcon} />
          <div className={styles.entry}>{ checkedItems.length } {checkedItems.length === 1 ? 'entry' : 'entries'}</div>
          <div className={styles.selected}>{ t('selected') }</div>
        </div>
        <div className={styles.sidebarFooter}>
          <button
            type="button"
            disabled={checkedItems.every(r => r.status === 'approved')}
            className={cn(styles.button, styles.approve)}
            onClick={() => handleChangeRequestStatusBulk('approved', checkedItems)}>
            { t('Approve') }
          </button>
          <button
            type="button"
            disabled={checkedItems.every(r => r.status === 'rejected')}
            className={cn(styles.button, styles.reject)}
            onClick={() => handleChangeRequestStatusBulk('rejected', checkedItems)}>
            { t('Reject') }
          </button>
        </div>
      </div>
      <ReactTooltip
        id='note'
        effect='solid'
        className={styles.tooltip} />
      <RequestBehalf
        ref={requestFormRef}
        title={t('Fill Request')}
        onSubmit={handleSubmitRequest}
        handleClose={() => {}}
        buttonTitle={t('Submit')}
        employees={[]} // {id: employee.id}
        policies={policies}
        initialValue={{}}
        activeTimeOff={1}
        singleRequest />
      <Dialog
        open={showDocuments}
        onClose={() => setShowDocuments(false)}
        >
        <div className={styles.dialogContent}>
          <div>{t('Attachment')}</div>
          <UploadFiles mode="view" ref={uploadFilesRef} />
        </div>
      </Dialog>
      {
        loading
          ? <div className={styles.overlayProgress}>
              <Progress />
            </div>
          : null
      }
    </div>
  )
}

export default TimneOffRequests
